/**
 * Page de sélection du type de dashboard
 * Permet à l'utilisateur de choisir entre Propriétaire et Locataire
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  Grid,
  Avatar,
  Stack
} from '@mui/material';
import {
  Business as BusinessIcon,
  Person as PersonIcon,
  Dashboard as DashboardIcon
} from '@mui/icons-material';

const DashboardSelectionPage = () => {
  const navigate = useNavigate();

  const handleSelection = (type) => {
    // Rediriger directement vers my-reservations pour les locataires
    if (type === 'renter') {
      navigate('/reservations-hub');
    } else if (type === 'owner') {
      navigate('/owner/reservations');
    } else {
      navigate(`/dashboard/${type}`);
    }
  };

  const dashboardOptions = [
    {
      type: 'owner',
      title: 'Propriétaire',
      description: 'Gérez vos équipements, réservations et revenus',
      icon: <BusinessIcon sx={{ fontSize: 40 }} />,
      color: 'primary',
      features: [
        'Suivi des réservations',
        'Gestion des équipements',
        'Statistiques de revenus',
        'Calendrier d\'occupation'
      ]
    },
    {
      type: 'renter',
      title: 'Locataire',
      description: 'Suivez vos locations et équipements loués',
      icon: <PersonIcon sx={{ fontSize: 40 }} />,
      color: 'secondary',
      features: [
        'Mes locations en cours',
        'Historique des locations',
        'Équipements favoris',
        'Dépenses totales'
      ]
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box textAlign="center" mb={6}>
        <Avatar 
          sx={{ 
            width: 80, 
            height: 80, 
            bgcolor: 'primary.main',
            mx: 'auto',
            mb: 3
          }}
        >
          <DashboardIcon sx={{ fontSize: 40 }} />
        </Avatar>
        
        <Typography variant="h3" component="h1" gutterBottom>
          Choisissez votre dashboard
        </Typography>
        
        <Typography variant="h6" color="text.secondary" maxWidth="600px" mx="auto">
          Sélectionnez le type de dashboard qui correspond à votre utilisation de la plateforme
        </Typography>
      </Box>

      <Grid container spacing={4} justifyContent="center">
        {dashboardOptions.map((option) => (
          <Grid item xs={12} md={6} key={option.type}>
            <Card 
              elevation={3}
              sx={{ 
                height: '100%',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: 6
                }
              }}
            >
              <CardActionArea 
                onClick={() => handleSelection(option.type)}
                sx={{ height: '100%', p: 3 }}
              >
                <CardContent sx={{ textAlign: 'center' }}>
                  <Avatar 
                    sx={{ 
                      width: 80, 
                      height: 80, 
                      bgcolor: `${option.color}.main`,
                      mx: 'auto',
                      mb: 3
                    }}
                  >
                    {option.icon}
                  </Avatar>
                  
                  <Typography variant="h4" component="h2" gutterBottom>
                    {option.title}
                  </Typography>
                  
                  <Typography variant="body1" color="text.secondary" mb={4}>
                    {option.description}
                  </Typography>

                  <Stack spacing={1} alignItems="center">
                    <Typography variant="h6" color="text.primary">
                      Fonctionnalités :
                    </Typography>
                    {option.features.map((feature, index) => (
                      <Typography 
                        key={index} 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ 
                          '&:before': { 
                            content: '"✓ "', 
                            color: `${option.color}.main`,
                            fontWeight: 'bold'
                          }
                        }}
                      >
                        {feature}
                      </Typography>
                    ))}
                  </Stack>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Box textAlign="center" mt={6}>
        <Typography variant="body2" color="text.secondary">
          Vous pourrez toujours changer de vue depuis votre dashboard
        </Typography>
      </Box>
    </Container>
  );
};

export default DashboardSelectionPage;
